// A project's Summary and Reports (spec §6.10, §6.11): the two RPCs of
// 20261016160000_project_reports.sql and the pure shaping their charts share.
// Every figure here is a SQL count or the sum of one; nothing is invented. The
// server answers only projects:full and people on the project, starts a Jira
// project's history at its link (`floor`), and sends the project's total
// hours only to its manager, the owner and admins (owner, Q1).
import { supabase } from './supabase';
import type { IssueType, StatusCategory } from './issues';

export type HistoryEvent = {
  at: string;
  task_id: string;
  key: string | null;
  summary: string;
  actor: string | null;
  actor_name: string | null;
  field: string;
  from: string | null;
  to: string | null;
};

export type ProjectSummary = {
  /** When a Jira project was linked: nothing before it is counted. */
  floor: string | null;
  today: string;
  /** The work items each card counts. */
  done: string[];
  created: string[];
  updated: string[];
  due: string[];
  /** The last 14 org days, oldest first. Null: before the link, not measured. */
  series: { done: (number | null)[]; created: (number | null)[]; updated: (number | null)[] };
  /** The next 7 days, today first. */
  due_series: number[];
  by_status: { status_id: string; name: string; category: StatusCategory; count: number }[];
  by_priority: { priority: string; count: number }[];
  by_type: { type: IssueType; count: number }[];
  workload: WorkloadRow[];
  recent: HistoryEvent[];
};

export type ReportKind =
  'created_resolved' | 'cumulative_flow' | 'workload' | 'time_tracking' | 'average_age';
export type Bucket = 'day' | 'week';

export type CreatedResolvedRow = {
  d: string;
  created: number;
  resolved: number;
  c_created: number;
  c_resolved: number;
};
export type FlowRow = { d: string; todo: number; progress: number; done: number };
export type AgeRow = { d: string; open: number; avg_days: number | null };
export type WorkloadRow = { assignee_id: string | null; count: number };
export type TimeRow = {
  task_id: string;
  key: string | null;
  summary: string;
  type: IssueType;
  epic_id: string | null;
  estimate: number;
  logged: number;
};
export type EpicTime = {
  epic_id: string;
  key: string | null;
  summary: string;
  estimate: number;
  logged: number;
};
export type TimeTracking = {
  items: TimeRow[];
  epics: EpicTime[];
  /** Only for the project's manager, the owner and admins. */
  total: { estimate: number; logged: number } | null;
};
export type ReportRows = {
  created_resolved: CreatedResolvedRow[];
  cumulative_flow: FlowRow[];
  average_age: AgeRow[];
  workload: WorkloadRow[];
  time_tracking: TimeTracking;
};
export type Report<K extends ReportKind> = {
  kind: K;
  /** The first day counted: the range's start, or a Jira project's link day. */
  from: string;
  to: string;
  bucket: Bucket;
  floor: string | null;
  rows: ReportRows[K];
};

export type Fetched<T> = { ok: true; data: T | null } | { ok: false; error: string };

// PGRST202: the function isn't deployed yet (this bundle ahead of the migration).
const message = (e: { code?: string; message: string }) =>
  e.code === 'PGRST202' ? 'Reports aren’t switched on yet.' : e.message;

/** Null data: you can't see this project. */
export async function fetchSummary(projectId: string): Promise<Fetched<ProjectSummary>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase.rpc('project_summary', { p_project: projectId });
  return error ? { ok: false, error: message(error) } : { ok: true, data: data as ProjectSummary };
}

export async function fetchReport<K extends ReportKind>(
  projectId: string,
  kind: K,
  from: string,
  to: string,
  bucket: Bucket = 'day',
): Promise<Fetched<Report<K>>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase.rpc('project_report', {
    p_project: projectId,
    p_kind: kind,
    p_from: from,
    p_to: to,
    p_bucket: bucket,
  });
  return error ? { ok: false, error: message(error) } : { ok: true, data: data as Report<K> };
}

/**
 * Whole percentages that add up to 100 (largest remainder), so a breakdown
 * never reads 33 + 33 + 33. All zero: all zero.
 */
export const percentages = (counts: number[]): number[] => {
  const total = counts.reduce((a, b) => a + b, 0);
  if (!total) return counts.map(() => 0);
  const raw = counts.map((c) => (c / total) * 100);
  const out = raw.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  const order = raw
    .map((r, i) => [r - Math.floor(r), i])
    .sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    out[i] += 1;
    left -= 1;
  }
  return out;
};

/**
 * The last 7 days of a 14-day series against the 7 before: "+3", "−2", "0".
 * Null when either week has a day that wasn't measured (before a Jira link).
 */
export const weekChange = (series: (number | null)[]): { text: string; diff: number } | null => {
  if (series.length < 14 || series.slice(-14).some((v) => v == null)) return null;
  const sum = (xs: (number | null)[]) => xs.reduce<number>((a, b) => a + (b ?? 0), 0);
  const diff = sum(series.slice(-7)) - sum(series.slice(-14, -7));
  return { text: diff > 0 ? `+${diff}` : diff < 0 ? `−${-diff}` : '0', diff };
};

/** Hours as Jira writes them: 6h, 1.5h, 0h. */
export const fmtHours = (h: number): string => `${Math.round(h * 10) / 10}h`;

const FIELD_NAMES: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  summary: 'Summary',
  due_date: 'Due date',
  start_date: 'Start date',
  labels: 'Labels',
  issue_type: 'Work type',
  parent: 'Parent',
  estimate: 'Original estimate',
  category: 'Category',
  team: 'Team',
};
const TYPE_WORD: Record<string, string> = {
  epic: 'Epic',
  story: 'Story',
  task: 'Task',
  bug: 'Bug',
  subtask: 'Subtask',
};

/**
 * One history row as the rest of a sentence after the actor's name ("changed
 * Status from To Do to In Progress"). `name` turns a profile id (assignee,
 * reporter) into a name; `day` formats a date key.
 */
export const describeEvent = (
  e: Pick<HistoryEvent, 'field' | 'from' | 'to'>,
  name: (id: string) => string,
  day: (key: string) => string,
): string => {
  const v = (x: string | null) =>
    x == null
      ? null
      : e.field === 'issue_type'
        ? (TYPE_WORD[x] ?? x)
        : e.field === 'due_date' || e.field === 'start_date'
          ? day(x)
          : e.field === 'estimate'
            ? fmtHours(Number(x))
            : x;
  switch (e.field) {
    case 'created':
      return 'created the work item';
    case 'description':
      return 'updated the Description';
    case 'assignee':
      return e.to ? `assigned it to ${name(e.to)}` : 'unassigned it';
    case 'reporter':
      return e.to ? `changed the Reporter to ${name(e.to)}` : 'cleared the Reporter';
    case 'attachment':
      return e.to ? `added the link ${e.to}` : `removed the link ${e.from ?? ''}`.trim();
    case 'link':
      return e.to ? `linked it: ${e.to}` : `removed the link: ${e.from ?? ''}`.trim();
    case 'comment_deleted':
      return 'deleted a comment';
  }
  const field = FIELD_NAMES[e.field] ?? e.field;
  const from = v(e.from);
  const to = v(e.to);
  if (from && to) return `changed ${field} from ${from} to ${to}`;
  if (to) return `set ${field} to ${to}`;
  return `cleared ${field}`;
};
